import useUser from "hooks/useuser";
import { useCallback } from "react";
import { User, useConfigCatClient } from "configcat-react";

/**
 * Custom hook for managing feature flags using ConfigCat service. Allows us to encapsulate the user
 * claims we want to pass to ConfigCat into one location.
 * @returns {Object} An object containing the getFlag function.
 * @returns {Function} getFlag - Async function to retrieve feature flag value.
 * @example
 * const { getFlag } = useFeatureFlag();
 * const isFeatureEnabled = await getFlag("myFeatureFlag", false);
 */
export default function useFeatureFlag() {
  const { user } = useUser();
  const configCatClient = useConfigCatClient();
  const getFlag = useCallback(
    async (flagName: string, defaultVal: boolean) => {
      const configCatUser = new User(user?.id ?? "", undefined, undefined, {
        districtId: user?.organization?.parentOrganization?.orgGuid ?? "",
        schoolId: user?.organization?.orgGuid ?? ""
      });

      try {
        const flagValue = await configCatClient.getValueAsync<boolean>(flagName, defaultVal, configCatUser);
        return flagValue;
      } catch (error) {
        console.error("Error fetching feature flag:", error);
        return defaultVal;
      }
    },
    [configCatClient, user]
  );

  return { getFlag };
}
